import DashboardLayout from '../../components/DashboardLayout';
import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import { batchStatusBadgeClass, formatStatusLabel } from '../../utils/batchStudentStatusHelpers';

// Same fallback convention as Timetable.jsx: placehold.co when there's no valid photo.
function StudentAvatar({ name, imagePath }) {
    const [imgError, setImgError] = useState(false);
    const hasImage = imagePath && !imgError;
    const src = hasImage
        ? getImageURL(imagePath)
        : `https://placehold.co/64x64?text=${encodeURIComponent(name || 'Student')}`;
    return (
        <img
            src={src}
            alt={name}
            onError={() => setImgError(true)}
            className="h-9 w-9 shrink-0 rounded-full border border-gray-200 bg-gray-100 object-cover"
        />
    );
}

// Side-by-side "before/after" so a reviewer can compare the student's
// existing live photo against what they're asking to replace it with.
function ImageCompareCell({ currentPath, requestedPath, onPreview }) {
    const [currentError, setCurrentError] = useState(false);

    return (
        <div className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-1">
                {currentPath && !currentError ? (
                    <img
                        src={getImageURL(currentPath)}
                        alt="Current profile photo"
                        title="Click to view larger"
                        className="h-14 w-14 cursor-pointer rounded-lg border border-gray-200 object-cover transition-opacity hover:opacity-80"
                        onClick={() => onPreview(getImageURL(currentPath))}
                        onError={() => setCurrentError(true)}
                    />
                ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-[10px] text-gray-400">
                        No photo
                    </div>
                )}
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Current</span>
            </div>

            <svg className="h-4 w-4 flex-shrink-0 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>

            <div className="flex flex-col items-center gap-1">
                <img
                    src={getImageURL(requestedPath)}
                    alt="Requested profile photo"
                    title="Click to view larger"
                    className="h-14 w-14 cursor-pointer rounded-lg border border-indigo-200 object-cover transition-opacity hover:opacity-80"
                    onClick={() => onPreview(getImageURL(requestedPath))}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
                <span className="text-[10px] font-medium uppercase tracking-wide text-indigo-500">Requested</span>
            </div>
        </div>
    );
}

const BASE_PATHS = {
    field: '/admin/students/profile-edit-requests',
    mobile: '/admin/students/mobile-number-requests',
    image: '/admin/students/image-edit-requests',
};

const TYPE_TABS = [
    { value: 'field', label: 'Profile Fields' },
    { value: 'mobile', label: 'Mobile Numbers' },
    { value: 'image', label: 'Profile Photos' },
];

const FIELD_LABELS = {
    fullName: 'Full name',
    email: 'Email',
    telephone: 'Telephone',
    dob: 'Date of birth',
    gender: 'Gender',
    nicNumber: 'NIC number',
    address: 'Address',
};

const STATUS_TABS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

export default function StudentProfileEditRequests() {
    const [type, setType] = useState('field');
    const [status, setStatus] = useState('pending');
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actioningId, setActioningId] = useState(null);
    const [search, setSearch] = useState('');
    const [previewImage, setPreviewImage] = useState(null);

    const basePath = BASE_PATHS[type];

    const fetchRequests = async (nextType, nextStatus) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(BASE_PATHS[nextType], { params: { status: nextStatus } });
            setRequests(res.data?.requests || []);
        } catch (error) {
            console.error('Error fetching requests:', error);
            Swal.fire('Error', 'Failed to load requests', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests(type, status);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, status]);

    const filteredRequests = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return requests;
        return requests.filter((r) => {
            const haystack = [
                r.studentName, r.studentId, r.registrationNumber, r.username, r.telephone,
                r.batchName, r.courseName, r.mobileNumber,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [requests, search]);

    const describeRequest = (request) => {
        if (type === 'mobile') {
            return `Mobile number for <b>${request.studentName || request.studentId}</b>:<br/><span style="color:#4f46e5">${request.mobileNumber}</span>`;
        }
        if (type === 'image') {
            const currentImg = request.imagePath
                ? `<img src="${getImageURL(request.imagePath)}" alt="Current photo" style="width:120px;height:120px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb;" />`
                : `<div style="width:120px;height:120px;display:flex;align-items:center;justify-content:center;border-radius:8px;border:1px dashed #d1d5db;color:#9ca3af;font-size:11px;">No photo</div>`;
            return `New profile photo for <b>${request.studentName || request.studentId}</b>:<br/>
                <div style="margin-top:8px;display:flex;align-items:center;justify-content:center;gap:12px;">
                  <div>${currentImg}<div style="margin-top:4px;font-size:11px;color:#9ca3af;">Current</div></div>
                  <span style="color:#9ca3af;">→</span>
                  <div><img src="${getImageURL(request.requestedImagePath)}" alt="Requested photo" style="width:120px;height:120px;object-fit:cover;border-radius:8px;border:1px solid #6366f1;" /><div style="margin-top:4px;font-size:11px;color:#4f46e5;">Requested</div></div>
                </div>`;
        }
        return `Update <b>${FIELD_LABELS[request.fieldName] || request.fieldName}</b> for <b>${request.studentName || request.studentId}</b> to:<br/><span style="color:#4f46e5">${request.newValue ?? '—'}</span>`;
    };

    const handleApprove = (request) => {
        Swal.fire({
            title: 'Approve this change?',
            html: describeRequest(request),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, approve',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActioningId(request.requestId);
            axiosInstance
                .post(`${basePath}/${request.requestId}/approve`)
                .then(() => {
                    setRequests((prev) => prev.filter((r) => r.requestId !== request.requestId));
                    Swal.fire('Approved', 'The change has been applied to the student record.', 'success');
                })
                .catch((error) => {
                    console.error('Error approving request:', error);
                    Swal.fire('Error', error.response?.data?.message || 'Failed to approve request', 'error');
                })
                .finally(() => setActioningId(null));
        });
    };

    const handleReject = (request) => {
        Swal.fire({
            title: 'Reject this change?',
            text: 'The student profile will not be updated.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, reject',
            input: 'text',
            inputPlaceholder: 'Optional reason',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActioningId(request.requestId);
            axiosInstance
                .post(`${basePath}/${request.requestId}/reject`, { reason: result.value || null })
                .then(() => {
                    setRequests((prev) => prev.filter((r) => r.requestId !== request.requestId));
                    Swal.fire('Rejected', 'The request has been rejected.', 'success');
                })
                .catch((error) => {
                    console.error('Error rejecting request:', error);
                    Swal.fire('Error', error.response?.data?.message || 'Failed to reject request', 'error');
                })
                .finally(() => setActioningId(null));
        });
    };

    // Every view has exactly one trailing column: "Actions" while pending, or
    // "Reviewed" (who/when/why) once approved or rejected.
    const columnCount = (type === 'field' ? 5 : 3) + 1;

    return (
        <DashboardLayout title="Student Profile Edit Requests">
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Student Profile Edit Requests</h1>
                    <p className="mt-1 text-sm text-gray-600">
                        Review and approve changes students requested to their own profile details.
                    </p>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-2">
                        {TYPE_TABS.map((tab) => (
                            <button
                                key={tab.value}
                                onClick={() => setType(tab.value)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                    type === tab.value
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        {STATUS_TABS.map((tab) => (
                            <button
                                key={tab.value}
                                onClick={() => setStatus(tab.value)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                    status === tab.value
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="relative max-w-sm flex-1">
                        <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                        </svg>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search student, ID, registration no, batch, course…"
                            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchRequests(type, status)}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                        <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                <div className="overflow-hidden rounded-lg bg-white shadow-sm border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Student</th>
                                {type === 'field' ? (
                                    <>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Field</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Current value</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Requested value</th>
                                    </>
                                ) : type === 'image' ? (
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Current vs. requested photo</th>
                                ) : (
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Mobile number</th>
                                )}
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Requested at</th>
                                {status === 'pending' ? (
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                                ) : (
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Reviewed</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {loading ? (
                                <tr>
                                    <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-gray-500">
                                        Loading requests...
                                    </td>
                                </tr>
                            ) : filteredRequests.length > 0 ? (
                                filteredRequests.map((request) => (
                                    <tr key={request.requestId} className="hover:bg-gray-50 transition-colors">
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <StudentAvatar name={request.studentName} imagePath={request.imagePath} />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-gray-900">{request.studentName || '—'}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {request.studentId}
                                                        {request.registrationNumber ? ` · ${request.registrationNumber}` : ''}
                                                        {request.telephone ? ` · ${request.telephone}` : ''}
                                                    </div>
                                                    {(request.batchName || request.courseName) && (
                                                        <div className="text-xs text-gray-400">
                                                            {request.batchName || '—'}
                                                            {request.batchName && request.courseName ? ' · ' : ''}
                                                            {request.courseName || ''}
                                                        </div>
                                                    )}
                                                    {request.batchDeliveryStatus && (
                                                        <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${batchStatusBadgeClass(request.batchDeliveryStatus)}`}>
                                                            {formatStatusLabel(request.batchDeliveryStatus)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        {type === 'field' ? (
                                            <>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                                                    {FIELD_LABELS[request.fieldName] || request.fieldName}
                                                </td>
                                                <td className="max-w-xs px-6 py-4 text-sm text-gray-500">{request.oldValue || '—'}</td>
                                                <td className="max-w-xs px-6 py-4 text-sm font-medium text-indigo-700">{request.newValue || '—'}</td>
                                            </>
                                        ) : type === 'image' ? (
                                            <td className="px-6 py-4">
                                                <ImageCompareCell
                                                    currentPath={request.imagePath}
                                                    requestedPath={request.requestedImagePath}
                                                    onPreview={setPreviewImage}
                                                />
                                            </td>
                                        ) : (
                                            <td className="max-w-xs px-6 py-4 text-sm font-medium text-indigo-700">{request.mobileNumber}</td>
                                        )}
                                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                            {request.requestedAt
                                                ? new Date(request.requestedAt).toLocaleDateString('en-GB', {
                                                      day: '2-digit',
                                                      month: 'short',
                                                      year: 'numeric',
                                                  })
                                                : '—'}
                                        </td>
                                        {status === 'pending' ? (
                                            <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleApprove(request)}
                                                        disabled={actioningId === request.requestId}
                                                        className="rounded-lg bg-green-100 px-3 py-1.5 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => handleReject(request)}
                                                        disabled={actioningId === request.requestId}
                                                        className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </td>
                                        ) : (
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                <div className="text-gray-700">{request.reviewedByName || '—'}</div>
                                                <div className="text-xs text-gray-400">
                                                    {request.reviewedAt
                                                        ? new Date(request.reviewedAt).toLocaleDateString('en-GB', {
                                                              day: '2-digit',
                                                              month: 'short',
                                                              year: 'numeric',
                                                          })
                                                        : '—'}
                                                </div>
                                                {status === 'rejected' && request.reviewNote && (
                                                    <div className="mt-0.5 max-w-xs text-xs italic text-red-600">"{request.reviewNote}"</div>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columnCount} className="px-6 py-12 text-center">
                                        <p className="text-sm text-gray-500">
                                            {search.trim() ? 'No requests match your search.' : `No ${status} requests.`}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {previewImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPreviewImage(null)}
                >
                    <button
                        type="button"
                        onClick={() => setPreviewImage(null)}
                        className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                        aria-label="Close preview"
                    >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <img
                        src={previewImage}
                        alt="Requested profile photo, full size"
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
                    />
                </div>
            )}
        </DashboardLayout>
    );
}
